import React, { useState, useRef } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { RiskCashSale, useUpdateCashSaleRows } from "@/lib/api/risk/cashSales/general";
import { UpdateCashSaleSchema, useUpdateManualCashSale } from "@/lib/api/risk/manualEntries"
import { useToast } from "@/components/ui/use-toast";
import { z } from "zod";
import moment from "moment";
import { CashSalesForm, CashSalesFormData } from "./CashSalesForm";
import { findFilteredDifferences, cleanObject } from "@/lib/prelude";

interface UpdateCashSalesFormProps {
    selectedRow: RiskCashSale | null;
    onSuccess: () => void;
    onCancel?: () => void;
    showCancelButton?: boolean;
}

// Standalone form component that can be used anywhere
export function UpdateCashSalesForm({ selectedRow, onSuccess, onCancel, showCancelButton = true }: UpdateCashSalesFormProps) {
    const { toast } = useToast();
    const updateCashSaleRows = useUpdateCashSaleRows();
    const updateManualCashSale = useUpdateManualCashSale();
    const [isUpdating, setIsUpdating] = useState(false);
    
    const handleSubmit = async (data: CashSalesFormData) => {
        if (!selectedRow) return;

        setIsUpdating(true);
        try {
            // Prepare current form data for comparison
            const currentData = cleanObject({
                status: data.status,
                contract_type: data.contract_type,
                contract_number: data.contract_number,
                contract_release: data.contract_release,
                location_id: data.location_id,
                customer_id: data.customer_id,
                market_zone_id: data.market_zone_id,
                trader_id: data.trader_id,

                destination: data.destination,
                washed_gain_loss: data.washed_gain_loss,

                add_date: data.add_date,
                start_date: data.start_date,
                end_date: data.end_date,
                note: data.note,
                commodity: data.commodity,
                crop_year: data.crop_year?.toString(),
                option_month: data.option_month,
                loads: data.loads,
                quantity: data.quantity,
                scheduled_quantity: data.scheduled_quantity,
                balance: data.balance,
                futures: data.futures,
                basis: data.basis,
                price: data.price,
            }, { removeEmptyStrings: true });

            // Find removed fields (fields that had a value but are now empty)
            const removedFields = findFilteredDifferences(
                currentData,
                selectedRow,
                { removed: true }
            );

            // Prepare data for API - convert dates to YYYY/MM/DD format as required by the API
            const updateData: any = {
                id: selectedRow.id,
                status: data.status,
                contract_type: data.contract_type,
                contract_number: data.contract_number,
                contract_release: data.contract_release,
                location_id: data.location_id || undefined,
                customer_id: data.customer_id,
                market_zone_id: data.market_zone_id,
                trader_id: data.trader_id,
                destination: data.destination,
                washed_gain_loss: data.washed_gain_loss,
                add_date: data.add_date,
                start_date: data.start_date || undefined,
                end_date: data.end_date || undefined,
                note: data.note || undefined,
                commodity: data.commodity,
                crop_year: data.crop_year,
                option_month: data.option_month || undefined,
                loads: data.loads || undefined,
                quantity: data.quantity,
                scheduled_quantity: data.scheduled_quantity || undefined,
                balance: data.balance || undefined,
                futures: data.futures || undefined,
                basis: data.basis || undefined,
                price: data.price || undefined,
            };

            // Set removed fields to null so they get cleared in the database
            removedFields.forEach(field => {
                // Skip required fields that shouldn't be nulled
                const requiredFields = ['status', 'contract_type', 'contract_number', 'contract_release', 
                                       'customer_id', 'add_date', 'commodity', 'crop_year', 'quantity'];
                if (!requiredFields.includes(field)) {
                    updateData[field] = null;
                }
            });

            // Use Zod safe parsing to validate the data
            const validationResult = UpdateCashSaleSchema.safeParse(updateData);

            if (!validationResult.success) {
                const errors = validationResult.error.errors.map(err => 
                    `${err.path.join('.')}: ${err.message}`
                ).join('\n');
                console.error("Failed to parse update cash sale", errors);

                toast({
                    title: "Invalid Data",
                    description: "",
                    variant: "destructive"
                });
                setIsUpdating(false);
                return;
            }

            const result = selectedRow.is_manually_inserted ? await updateManualCashSale(selectedRow.id, validationResult.data) : await updateCashSaleRows({
                ...validationResult.data,
                id: selectedRow.id
            } as any);

            if (!result.success) {
                throw result;
            }

            toast({ title: "Cash sale updated successfully" });
            onSuccess();
        } catch (error) {
            console.error("Failed to update cash sale", error);
            toast({
                title: "Failed to update cash sale",
                description: error instanceof Error ? error.message : "An unexpected error occurred",
                variant: "destructive"
            });
        } finally {
            setIsUpdating(false);
        }
    };

    if (!selectedRow) return null;

    return (
        <CashSalesForm
            mode="update"
            initialData={selectedRow}
            onSubmit={handleSubmit}
            onCancel={onCancel}
            showCancelButton={showCancelButton}
            isSubmitting={isUpdating}
        />
    );
}

interface UpdateCashSalesFormDialogProps {
    isOpen: boolean;
    onClose: () => void;
    selectedRow: RiskCashSale | null;
    onSuccess: () => void;
}

// Dialog component that wraps the form
export function UpdateCashSalesFormDialog({ isOpen, onClose, selectedRow, onSuccess }: UpdateCashSalesFormDialogProps) {
    const handleSuccess = () => {
        onSuccess();
        onClose();
    };

    if (!selectedRow) return null;

    return (
        <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
            <DialogContent className="sm:max-w-[90vw] md:max-w-[80vw] lg:max-w-[70vw] xl:max-w-[60vw] 2xl:max-w-[50vw] max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle>Update Cash Sale</DialogTitle>
                    <DialogDescription>
                        Update the details for contract {selectedRow.contract_number} release {selectedRow.contract_release}
                    </DialogDescription>
                </DialogHeader>

                <UpdateCashSalesForm
                    selectedRow={selectedRow}
                    onSuccess={handleSuccess}
                    onCancel={onClose}
                    showCancelButton={true}
                />
            </DialogContent>
        </Dialog>
    );
}
